import { WaterCharacteristicsParameters } from "@imi-ernd-be/hyselect-engine";
import { WaterCharacteristicsPreference } from "@src/core/api/waterCharacteristicsPreferences";
import { isNotNil } from "@src/shared/helpers/guards";
import { setIfNotNil } from "@src/shared/helpers/setIfNotNil";
import { assert } from "radashi";
import { engine } from "../..";
import { getNumberDefinition } from "../../helpers/fieldDefinitions/getNumberDefinition";
import { getOptionsGroupDefinition } from "../../helpers/fieldDefinitions/getOptionsGroupDefinition";
import { TextFieldDefinition } from "../../helpers/fieldDefinitions/getTextDefinition";
import { trackLiveObjects } from "../../helpers/trackLiveObjects";
import { buildValidationResult } from "../../validationResults/buildValidationResult";
import { toArray } from "../../vector";
import {
  buildEngineWaterCharacteristics,
  WaterCharacteristics,
} from "./buildEngineWaterCharacteristics";

export type WaterCharacteristicsDefinitions = ReturnType<
  typeof waterCharacteristicsRepository.getDefinitions
>;

export const waterCharacteristicsRepository = trackLiveObjects(
  "waterCharacteristicsRepository",
  {
    getDefinitions(values?: WaterCharacteristics) {
      const technicalParameters = new engine.TechnicalParameters();
      const waterCharacteristics =
        technicalParameters.getWaterCharacteristicsParams();
      assert(
        waterCharacteristics,
        "waterCharacteristics is null in waterCharacteristicsRepository.getDefinitions",
      );
      buildEngineWaterCharacteristics(waterCharacteristics, values);
      const waterInformation = waterCharacteristics.getWaterCharInformation();
      assert(
        waterInformation,
        "waterInformation is null in waterCharacteristicsRepository.getDefinitions",
      );

      const results = {
        additives: getOptionsGroupDefinition(
          waterCharacteristics.getWaterCharAdditiveFamily(),
        ),
        defaultDifferentialTemperature: getNumberDefinition(
          waterCharacteristics.getDiffTemp(),
        ),
        density: getNumberDefinition(waterInformation.getDensity()),
        freezingPoint: getNumberDefinition(waterInformation.getFreezPoint()),
        kinematicViscosity: getNumberDefinition(
          waterInformation.getKinViscosity(),
        ),
        mode: {
          default: "predefined",
          disabled: false,
          type: "text",
        } satisfies TextFieldDefinition,
        specificHeat: getNumberDefinition(waterInformation.getSpecificHeat()),
        supplyTemperature: getNumberDefinition(
          waterCharacteristics.getSupplyTemp(),
        ),
        weight: getNumberDefinition(waterCharacteristics.getPcWeight()),
      };
      technicalParameters.delete();
      return results;
    },
    getWaterInformation(values: WaterCharacteristics | undefined) {
      const technicalParameters = new engine.TechnicalParameters();
      const waterCharacteristics =
        technicalParameters.getWaterCharacteristicsParams();
      assert(
        waterCharacteristics,
        "waterCharacteristics is null in waterCharacteristicsRepository.getWaterInformation",
      );
      buildEngineWaterCharacteristics(waterCharacteristics, values);

      const waterCharacteristicsErrors = toArray(
        waterCharacteristics.getErrorFields(),
      )
        .map(buildValidationResult)
        .filter(isNotNil);
      if (waterCharacteristicsErrors.length) {
        technicalParameters.delete();
        return { errors: waterCharacteristicsErrors, status: "error" as const };
      }

      const waterInformation = waterCharacteristics.getWaterCharInformation();
      assert(
        waterInformation,
        "waterCharInformation is null in waterCharacteristicsRepository.getWaterInformation",
      );

      const results = {
        density: {
          ...getNumberDefinition(waterInformation.getDensity()),
          valueSI: waterInformation.getDensity()?.valueSI ?? 0,
        },
        freezingPoint: {
          ...getNumberDefinition(waterInformation.getFreezPoint()),
          valueSI: waterInformation.getFreezPoint()?.valueSI ?? 0,
        },
        kinematicViscosity: {
          ...getNumberDefinition(waterInformation.getKinViscosity()),
          valueSI: waterInformation.getKinViscosity()?.valueSI ?? 0,
        },
        specificHeat: {
          ...getNumberDefinition(waterInformation.getSpecificHeat()),
          valueSI: waterInformation.getSpecificHeat()?.valueSI ?? 0,
        },
        waterPhase: waterInformation.waterPhase.value as number,
      };
      technicalParameters.delete();
      return { data: results, status: "success" as const };
    },
    init(
      waterCharacteristics: WaterCharacteristicsParameters,
      userWaterCharacteristics: WaterCharacteristicsPreference,
    ) {
      if (userWaterCharacteristics.mode === "manual") {
        waterCharacteristics.disableWaterCharAdditFamily();

        const waterInformation = waterCharacteristics.getWaterCharInformation();
        assert(
          waterInformation,
          "waterInformation is null in waterCharacteristicsRepository.init",
        );
        setIfNotNil(
          waterInformation.setWaterCharDensity.bind(waterInformation),
          userWaterCharacteristics.density,
        );
        setIfNotNil(
          waterInformation.setWaterCharFreezPoint.bind(waterInformation),
          userWaterCharacteristics.freezingPoint,
        );
        setIfNotNil(
          waterInformation.setWaterCharKinViscosity.bind(waterInformation),
          userWaterCharacteristics.kinematicViscosity,
        );
        setIfNotNil(
          waterInformation.setWaterCharSpecificHeat.bind(waterInformation),
          userWaterCharacteristics.specificHeat,
        );

        waterCharacteristics.setWaterCharInformation(waterInformation);
      } else {
        buildEngineWaterCharacteristics(
          waterCharacteristics,
          userWaterCharacteristics,
        );
      }
    },
  },
);
